import * as THREE from "three";
import { CONFIG } from "../config";

export type TapCallback = (clientX: number, clientY: number) => void;

export class InputHandler {
  private readonly raycaster = new THREE.Raycaster();
  private readonly mouse = new THREE.Vector2();
  private readonly threshold = CONFIG.input.tapMoveThreshold;
  private readonly canvas: HTMLCanvasElement;
  private readonly camera: THREE.Camera;
  private readonly onTap: TapCallback;

  private pointerDownX = 0;
  private pointerDownY = 0;
  private pointerDownId: number | null = null;

  private touchDownX = 0;
  private touchDownY = 0;
  private touchDownId: number | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    camera: THREE.Camera,
    onTap: TapCallback,
  ) {
    this.canvas = canvas;
    this.camera = camera;
    this.onTap = onTap;
    this._bindCanvasEvents();
  }

  private emitTap(clientX: number, clientY: number): void {
    this.onTap(clientX, clientY);
  }

  private _bindCanvasEvents(): void {
    this.canvas.addEventListener("pointerdown", this._onPointerDown);
    this.canvas.addEventListener("touchstart", this._onTouchStart, {
      passive: true,
      capture: true,
    });
  }

  bindWindowEvents(): void {
    window.addEventListener("pointerup", this._onPointerUp);
    window.addEventListener("touchend", this._onTouchEnd, {
      passive: true,
      capture: true,
    });
  }

  unbindWindowEvents(): void {
    window.removeEventListener("pointerup", this._onPointerUp);
    window.removeEventListener("touchend", this._onTouchEnd);
  }

  private _onPointerDown = (e: PointerEvent): void => {
    this.pointerDownX = e.clientX;
    this.pointerDownY = e.clientY;
    this.pointerDownId = e.pointerId;
  };

  private _onPointerUp = (e: PointerEvent): void => {
    if (this.pointerDownId !== e.pointerId) return;
    this.pointerDownId = null;
    if (e.pointerType === "touch") return;

    const moved = Math.hypot(
      e.clientX - this.pointerDownX,
      e.clientY - this.pointerDownY,
    );
    if (moved > this.threshold) return;

    this.emitTap(e.clientX, e.clientY);
  };

  private _onTouchStart = (e: TouchEvent): void => {
    if (e.touches.length === 1) {
      this.touchDownX = e.touches[0]!.clientX;
      this.touchDownY = e.touches[0]!.clientY;
      this.touchDownId = e.touches[0]!.identifier;
    }
  };

  private _onTouchEnd = (e: TouchEvent): void => {
    if (this.touchDownId === null || e.changedTouches.length === 0) return;
    const touch = Array.from(e.changedTouches).find(
      (t) => t.identifier === this.touchDownId,
    );
    if (!touch) return;
    this.touchDownId = null;

    const moved = Math.hypot(
      touch.clientX - this.touchDownX,
      touch.clientY - this.touchDownY,
    );
    if (moved > this.threshold) return;

    this.emitTap(touch.clientX, touch.clientY);
  };

  /**
   * Raycast from screen coordinates to find intersection with target.
   * Returns null if no intersection.
   */
  raycast(
    clientX: number,
    clientY: number,
    target: THREE.Object3D,
  ): THREE.Intersection | null {
    const { width, height } = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    this.mouse.x = (clientX / width) * 2 - 1;
    this.mouse.y = -(clientY / height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);

    const intersects = this.raycaster.intersectObject(target, true);
    return intersects[0] ?? null;
  }

  /**
   * Raycast and return the first hit that matches the predicate (e.g. a reference point).
   * Falls back to the closest hit if no match.
   */
  raycastFirst(
    clientX: number,
    clientY: number,
    target: THREE.Object3D,
    predicate?: (hit: THREE.Intersection) => boolean,
  ): THREE.Intersection | null {
    const { width, height } = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
    this.mouse.x = (clientX / width) * 2 - 1;
    this.mouse.y = -(clientY / height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);

    const intersects = this.raycaster.intersectObject(target, true);

    if (predicate) {
      const match = intersects.find(predicate);
      return (
        match ??
        intersects?.find((h) => !(h.object as THREE.Sprite).isSprite) ??
        null
      );
    }

    return (
      intersects.filter((h) => !(h.object as THREE.Sprite).isSprite)[0] ?? null
    );
  }
}
